import { Dispatch, SetStateAction, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { runSignatures, UserKeys } from '../../../api/users-api';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  isAbleToSeeUIElement,
  ToolsRoles,
} from '../../../utils/roles/client-roles';
import { SignatureModel } from '../../../../types';
import Typography from '@mui/material/Typography';

interface RunSignatureTemplatesDialogProps {
  open: boolean;
  handleClose: () => void;
  selectedRows: number[];
  setSelectedRows: Dispatch<SetStateAction<number[]>>;
  changeLogRoles: string[];
  signatures: SignatureModel[] | undefined;
}

export default function RunSignatureTemplatesDialog({
  open,
  handleClose,
  selectedRows,
  setSelectedRows,
  changeLogRoles,
  signatures,
}: RunSignatureTemplatesDialogProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  // const queryClient = useQueryClient();

  const selectedSignatures = signatures?.filter((item) =>
    selectedRows.includes(item.id)
  );

  const { mutate, isPending } = useMutation({
    mutationFn: () => runSignatures(selectedSignatures!, changeLogMessage),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message:
          selectedRows.length === 1
            ? 'The signature template is running.'
            : 'The signature templates are running.',
      });
      queryClient.invalidateQueries({ queryKey: [UserKeys.Signatures] });
      queryClient.invalidateQueries({ queryKey: [UserKeys.SignatureRunLogs] });
      setSelectedRows([]);
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage:
          selectedRows.length === 1
            ? 'There was an issue running the selected template. Please refresh and try again.'
            : 'There was an issue running the selected templates. Please refresh and try again.',
      });
    },
    onSettled: () => {
      createEvent(
        analytics.automation.signature_templates.run_signature_template,
        {
          module: ModuleList.modules.automation.name,
          section: ModuleList.modules.automation.sections.signatureTemplates,
        }
      );
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (
      isAbleToSeeUIElement(changeLogRoles, ToolsRoles.MaunalRunTemplate.value)
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  const isBulk = selectedRows.length > 1;
  const templateName = selectedSignatures?.[0]?.templateName ?? '';

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title={isBulk ? 'Run Signature Templates' : 'Run Signature Template'}
      dialogContent={
        <>
          <Typography>
            {isBulk ? (
              <>
                Run <strong>{selectedRows.length} signature templates</strong>.
              </>
            ) : (
              <>
                Run <strong>{templateName}</strong>.
              </>
            )}
          </Typography>
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isPending}
          />
        </>
      }
      primaryButton={{
        title: isBulk ? 'Run Templates' : 'Run Template',
        action: handleSubmit,
        loading: isPending,
        disabled: isPending,
        color: 'primary',
      }}
    />
  );
}
